import type { View, ViewContext } from '../router';
import { compareSurface } from '../components/compare';
import { isFacet } from '../../core/compare';

// #/compare/:left/:right — the side-by-side view as a place you can be.
//
// The surface itself lives in src/app/components/compare.ts, because a quiz
// mounts the same thing as an overlay rather than as a route (a round is state
// in a view's closure, and navigating away from one throws it away). This file
// is only the routed mount: where "back" goes, and how walking around inside
// the view is written back into the URL.
//
// The pair is in the PATH and the facet is a QUERY pair, which is the split
// the router already draws — the path says what this screen IS, the query says
// a fact about the visit. Every facet is on one scroll now, so the query no
// longer names a tab to open: it names the section to scroll to, which is how
// a Sound ID round hands over the voice it just played. It is read on arrival
// and never written back, because where you have scrolled to is not part of
// which two birds you are comparing.

export const compareView: View = (root: HTMLElement, ctx: ViewContext) => {
  const facet = isFacet(ctx.params.facet) ? ctx.params.facet : undefined;

  const surface = compareSurface({
    left: ctx.params.left,
    right: ctx.params.right,
    facet,
    // `rewrite`, not `replace`: the surface has already repainted itself by the
    // time this runs, so re-entering the view would rebuild both sides to say
    // something they already say. And no history entry per step — a learner
    // walking one side along a family is making ONE visit, and back should
    // leave the comparison rather than unwind it a swipe at a time.
    onChange: (a, b) => ctx.rewrite(`/compare/${a}/${b}`),
    // Wherever this was opened from — the species page with its scroll and its
    // gallery intact, or the Library behind it — and the Library on a cold
    // load from a shared link, which is the only screen a comparison of two
    // birds could sensibly fall back to.
    onBack: () => ctx.back('/library'),
  });

  root.append(surface.element);
  // Only now does the section list have offsets to scroll to.
  surface.reveal();
  return surface.destroy;
};
